import React, { Component } from 'react';
import msAppSource from './assets/MS_AppSource.webp';
import './pentities.css';
import './pShareEmail.css';
import NavBar from './NavBar';

interface PShareEmailPageProps {}

interface PShareEmailPageState {
  isVideoOpen: boolean;
}

class PShareEmailPage extends Component<PShareEmailPageProps, PShareEmailPageState> {
  constructor(props: PShareEmailPageProps) {
    super(props);
    this.state = { isVideoOpen: false };
  }

  openVideo = () => this.setState({ isVideoOpen: true });
  closeVideo = () => this.setState({ isVideoOpen: false });

  render() {
    return (
      <div className="pentities-page pshareemail-v2-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="pentities-hero-section">
          <div className="pentities-hero-video-background">
            <video preload="metadata" autoPlay loop muted playsInline className="pentities-hero-video">
              <source src={require('./assets/video/web/15024041_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="pentities-container">
            <div className="pentities-hero-content">
              <span className="pentities-hero-eyebrow">Outlook Add-in</span>
              <h1 className="pentities-hero-title">
                Save, share and manage every Outlook email inside Microsoft 365
              </h1>
              <p className="pentities-hero-description">
                PShareEmail turns Outlook into a control centre for your content. Save emails to SharePoint,
                OneDrive or Teams, navigate every library from one pane, create shareable links, and track a
                sender's full history without ever leaving your inbox.
              </p>
              <div className="pentities-hero-cta">
                <a
                  href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pentities-hero-cta-btn"
                >
                  Request Demo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* General Description */}
        <section id="overview" className="pentities-gendesc-section">
          <div className="pentities-container">
            <div className="pentities-gendesc-content">
              <span className="pentities-gendesc-label">What is PShareEmail?</span>
              <h2 className="pentities-gendesc-title">Your inbox, connected to everything in Microsoft 365</h2>
              <p>
                PShareEmail is a powerful Outlook add-in that changes how you handle email. Pick a destination in
                any document library and it automatically creates a folder named after the email subject and the
                time it was sent, containing an <b>originalMail.pdf</b> of the message together with every
                attachment a clean, self-contained record every time.
              </p>
              <p>
                <b>But it does far more than save.</b> Send a message and its attachments to the tags of a Teams
                channel, or straight into a chat with specific team members. Browse OneDrive, Teams channel
                libraries and SharePoint sites to find documents and drop shareable links into new emails all
                from a single pane inside Outlook.
              </p>
              <p>
                <b>Native to Microsoft 365.</b> Built with the Office JavaScript library and Microsoft Graph, it
                runs directly against your own tenant with enterprise-grade security no separate portal, no data
                leaving your control.
              </p>
            </div>
          </div>
        </section>

        {/* Core Capabilities dark section */}
        <section id="capabilities" className="pentities-docmgmt-section">
          <div className="pentities-container">
            <span className="pentities-docmgmt-eyebrow">Core capabilities</span>
            <h2 className="pentities-docmgmt-title">Everything email management needs, in one Outlook pane</h2>
            <p className="pentities-docmgmt-lead">
              Navigate, save, share and trace without switching apps or breaking your flow.
            </p>

            <div className="pentities-docmgmt-block">
              <h3>1 · Navigate your whole Microsoft 365</h3>
              <p>
                Open PShareEmail from the Outlook ribbon and see three sections OneDrive, Teams and SharePoint
                sites then drill into the libraries of each location to find exactly what you need.
              </p>
              <ul>
                <li><b>One interface</b> OneDrive, Teams channel libraries and every SharePoint site you can access.</li>
                <li><b>Folder insight</b> click a path header to see its metadata, or open a folder to list its files.</li>
                <li><b>Pin locations</b> keep the destinations you use most one click away.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>2 · Save emails as organised entities</h3>
              <p>
                Every saved email becomes a tidy, self-describing record the original message as a PDF plus all
                attachments, filed in a folder named by subject and datetime, with metadata you can set as you go.
              </p>
              <ul>
                <li><b>Automatic structure</b> folders named by subject and time, with originalMail.pdf and attachments together.</li>
                <li><b>Flexible formats</b> save as PDF, as .eml body, or the complete email with attachments as .eml.</li>
                <li><b>At scale</b> save multiple emails, bulk-save attachments with metadata, and check for duplicates.</li>
                <li><b>Metadata &amp; content types</b> set default values and auto-create columns for From, To, Subject and more.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>3 · Share straight to Teams</h3>
              <p>
                Move a conversation into collaboration without forwarding. Send the selected email and its
                attachments exactly where they belong.
              </p>
              <ul>
                <li><b>Channel tags</b> send to specific tags of a channel, or to selected users within that tag.</li>
                <li><b>Direct chat</b> send to specific team members in a new chat, attachments included.</li>
                <li><b>Channel libraries</b> browse channels and their document libraries in the same pane.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>4 · Shareable links &amp; document preview</h3>
              <p>
                Find a document anywhere in Microsoft 365 and share it as a link, or preview it without leaving
                Outlook.
              </p>
              <ul>
                <li><b>One-click links</b> create view-only shareable links copied straight to your clipboard.</li>
                <li><b>Insert anywhere</b> drop links into new emails or a Teams chat.</li>
                <li><b>Preview in place</b> open and work with documents directly inside Outlook.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>5 · Sender history at a glance</h3>
              <p>
                Select any email, choose Sender History, and instantly see every message received from that
                sender preview or open any of them with a single click. Perfect for prepping a meeting or a
                follow-up.
              </p>
            </div>
          </div>
        </section>

        {/* Why choose white section */}
        <section id="why" className="pentities-workflow-section">
          <div className="pentities-container">
            <span className="pentities-workflow-eyebrow">Why teams choose it</span>
            <h2 className="pentities-workflow-title">Less inbox chaos, more organised, shareable, compliant email</h2>
            <p className="pentities-workflow-lead">
              PShareEmail removes the daily friction of filing, finding and forwarding and does it with the
              security your organisation already relies on.
            </p>

            <div className="pentities-workflow-block">
              <h3>Organised email storage</h3>
              <p>
                Automatic folder structures with subject and timestamp keep related emails and attachments together
                as entities easy to retrieve, easy to audit.
              </p>
            </div>
            <div className="pentities-workflow-block">
              <h3>Seamless collaboration</h3>
              <p>
                Share emails and attachments directly to Teams channels or specific users, and create shareable
                links instantly instead of forwarding the same message again and again.
              </p>
            </div>
            <div className="pentities-workflow-block">
              <h3>Unified access</h3>
              <p>
                Reach OneDrive, Teams and SharePoint from one interface no more hopping between apps to save an
                email or find a document.
              </p>
            </div>
            <div className="pentities-workflow-block">
              <h3>Metadata control &amp; compliance</h3>
              <p>
                Update metadata and content types as you save, apply default values, and keep categorisation
                consistent with retention and regulatory policies.
              </p>
            </div>
            <div className="pentities-workflow-block">
              <h3>Complete email history</h3>
              <p>
                View every email from any sender with one click, keeping full context across your interactions.
              </p>
            </div>
            <div className="pentities-workflow-block">
              <h3>Enterprise security</h3>
              <p>
                Built on Microsoft Entra ID with enterprise-grade security encrypted, and aligned with your
                Microsoft 365 compliance standards.
              </p>
            </div>
          </div>
        </section>

        {/* Platform & complete feature set dark */}
        <section id="platform" className="pentities-docmgmt-section">
          <div className="pentities-container">
            <span className="pentities-docmgmt-eyebrow">Platform support &amp; complete feature set</span>
            <h2 className="pentities-docmgmt-title">Runs everywhere Outlook does with the full toolkit</h2>

            <div className="pentities-docmgmt-block">
              <h3>Supported environments</h3>
              <ul>
                <li>Outlook for Windows, macOS and the Web</li>
                <li>New Outlook</li>
                <li>Outlook for Android and iOS</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>Search &amp; navigate</h3>
              <ul>
                <li>SharePoint Online, Teams and OneDrive for Business all from inside the app</li>
                <li>Outlook mails via Sender History</li>
                <li>Navigate every SharePoint library, Team / channel / files, and OneDrive</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>Save features</h3>
              <ul>
                <li>Save to libraries, folders, Document Sets and lists including sent emails</li>
                <li>Save as PDF, as .eml body, or the complete email with attachments as .eml</li>
                <li>Rename on save, save multiple emails, and bulk-save attachments with metadata</li>
                <li>Check for duplicates before saving</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>Advanced features</h3>
              <ul>
                <li>Pin locations and pin emails into the Entities app</li>
                <li>Update metadata &amp; content types, with default values on SharePoint fields</li>
                <li>Auto-create SharePoint columns for email attributes (From, To, Subject, etc.)</li>
                <li>Create shareable links, preview documents, and open any location in the browser</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Demo Video Section dark */}
        <section className="demo-video-section">
          <div className="pentities-container">
            <div className="pentities-demo-layout">
              <div className="pentities-demo-content">
                <span className="pentities-demo-eyebrow">See PShareEmail in Action</span>
                <h2 className="pentities-demo-title">
                  Watch how PShareEmail transforms everyday email management
                </h2>
                <p>
                  In a few minutes you'll see an email saved as a clean entity, an attachment shared into a Teams
                  channel, a document link created without leaving Outlook, and a sender's full history opened with
                  a single click.
                </p>
                <p>
                  Everything shown runs inside your own Microsoft 365 tenant, using the permissions your people
                  already have.
                </p>
              </div>
              <div className="pentities-demo-media">
                <div
                  className="pentities-demo-video-wrapper"
                  onClick={this.openVideo}
                  role="button"
                  tabIndex={0}
                  aria-label="Play PShareEmail product presentation"
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') this.openVideo(); }}
                >
                  <img loading="lazy" decoding="async"
                    className="pentities-demo-video-thumb"
                    src="https://img.youtube.com/vi/UuVcMDTDDfE/maxresdefault.jpg"
                    alt="PShareEmail Product Presentation"
                  />
                  <div className="pentities-demo-play-overlay">
                    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" className="pentities-demo-play-icon">
                      <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.55)" />
                      <polygon points="24,16 52,32 24,48" fill="#ffffff" />
                    </svg>
                  </div>
                </div>
                <p className="pentities-demo-video-caption">Click to watch the full presentation</p>
              </div>
            </div>
            <div className="download-section">
              <h3 className="download-title">Get PShareEmail Now</h3>
              <p className="download-description">
                Download PShareEmail directly from Microsoft AppSource and start transforming your email management today.
              </p>
              <a
                href="https://marketplace.microsoft.com/en-us/product/WA200007890?tab=Overview"
                target="_blank"
                rel="noopener noreferrer"
                className="appsource-link"
              >
                <img loading="lazy" decoding="async" src={msAppSource} alt="Get it from Microsoft AppSource" className="appsource-badge" />
              </a>
            </div>
          </div>
        </section>

        {/* Explore the platform modules grid */}
        <section id="explore" className="pentities-modules-section">
          <div className="pentities-container">
            <span className="pentities-modules-eyebrow">Explore the platform</span>
            <h2 className="pentities-modules-title">PShareEmail is one piece of a connected Microsoft 365 suite</h2>
            <p className="pentities-modules-lead">
              Pair it with the rest of Meta‑365 to unify content, search and automation across your tenant.
            </p>

            <div className="pentities-modules-grid">
              <a href="/solutions/pentities" className="pentities-module-card">
                <h3>pEntities Platform</h3>
                <p>
                  Turn contracts, cases, HR records and projects into living workspaces inside Microsoft Teams.
                  Every record carries its documents, emails, tasks and approvals in one place, with a domain AI
                  agent that reads the data you already own and surfaces the risks and next steps that matter.
                </p>
                <span className="pentities-module-link">Explore pEntities →</span>
              </a>
              <a href="/solutions/docsearch" className="pentities-module-card">
                <h3>DocSearch</h3>
                <p>
                  Unified, permission-aware search and record management across OneDrive, SharePoint and Teams.
                  Find any file, email or list item by content, metadata or type from a single box no matter
                  where it lives and organise, preview and act on results without ever leaving Microsoft 365.
                </p>
                <span className="pentities-module-link">Explore DocSearch →</span>
              </a>
              <a href="/solutions/searchassistant" className="pentities-module-card">
                <h3>Search Assistant</h3>
                <p>
                  Find and act on the right content faster, with an assistant that works in the flow of your
                  Microsoft 365 tools. Ask in natural language, get answers drawn from your own tenant, and jump
                  straight to the file, email or record you need right where you already work.
                </p>
                <span className="pentities-module-link">Explore Search Assistant →</span>
              </a>
              <a href="/solutions/automation" className="pentities-module-card">
                <h3>Workflow Automation</h3>
                <p>
                  No-code workflows that trigger on your own Microsoft 365 data and run themselves. Build
                  multi-step approvals, notifications and scheduled routines that react to changes across the
                  suite automating the repetitive work your teams do every day without writing any code.
                </p>
                <span className="pentities-module-link">Explore Automation →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Video Modal */}
        {this.state.isVideoOpen && (
          <div className="pentities-video-modal" onClick={this.closeVideo} role="dialog" aria-modal="true" aria-label="PShareEmail product presentation">
            <button className="pentities-video-close" onClick={this.closeVideo} aria-label="Close video">✕</button>
            <div className="pentities-video-frame" onClick={(e) => e.stopPropagation()}>
              <iframe
                src="https://www.youtube.com/embed/UuVcMDTDDfE?autoplay=1&rel=0"
                title="PShareEmail Product Presentation"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default PShareEmailPage;
